// Google Ads write-back: overlay a review grid's pending scalar edits and
// list operations onto a launcher result (the launch template the campaign
// launcher wrote to parsed_response.json).
//
// ONE module owns every Google Ads write. Both grids that render these plans
// go through it:
//   - ../reviewEdits.js#applyLauncherResultEdits (CLW review pane +
//     LaunchCampaignDetails), which owns the deep clone;
//   - ../../CopyCampaignWorkflow/copyReviewEdits.js, which imports the shared
//     writers below (applyGadsCampaignFields / writeGadsList /
//     rebuildGadsKeywords / applyGadsDisplayPath / parseGadsTargetLabel) for
//     the copy template's own field routing and then delegates the rest here.
// These helpers mutate the clone in place, exactly like the TTD / DV360
// appliers next to them.
//
// THE WRITE TARGET IS WHAT THE ROW BUILDER READS (./gadsRowBuilders.js) —
// every writer here targets the exact key buildGadsReviewRows renders, so a
// saved edit re-renders as typed.
//
// Row addressing: Google Ads plans carry no entity ids before launch, so
// buildGadsReviewRows assigns positional rowIds — `gads-{campaignIdx}-{unitIdx}`
// where the unit is an ad group (Search) or an asset group (PMax / Demand Gen).
// Both indices are stable for a given plan because the builders walk the arrays
// in order.
//
// SCOPE: Google Ads has no ad-group budget, flight or status — those live on
// the campaign. Cells backed by campaign keys (market / dates / budgets /
// pacing / status / bid strategy / target / country / languages / demographics
// / geo / zip codes) are therefore CAMPAIGN-SCOPED: editing one row rewrites
// every sibling row of the same campaign, by construction. The grid shows the
// campaign total's PER-UNIT share in the `budget` cell, so that one write
// scales back up by unit count (share × units) — the same contract TTD's
// budget writer uses.

import { gadsGeoIncludeEntries, gadsResultSubtype, keywordText } from './gadsRowBuilders';
import { numeric, scaleToParent, normalizeRowOps } from '../sharedFieldWriters';
import { applyGadsRowOps } from '../rowOps';

const asArray = (v) => (Array.isArray(v) ? v : []);
const nonEmpty = (v) => (v == null ? null : (String(v).trim() || null));

const microsFrom = (v) => {
  const n = numeric(v);
  return n == null ? null : Math.round(n * 1e6);
};

// Comma-separated cell (languages, demographics) → trimmed entries.
const splitCommaDraft = (draft) => String(draft || '')
  .split(',').map((s) => s.trim()).filter((s) => s && s !== '—');

// ' · '-joined list cell → its entries (the separator the row builders join
// list cells with).
const splitListDraft = (draft) => String(draft || '')
  .split('·').map((s) => s.trim()).filter(Boolean);

// 'YYYY-MM-DD' — the native date editor's output and the spelling the Google
// Ads templates carry. Anything else is a no-op (null).
const ymd = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/** Parse `gads-{campaignIdx}-{unitIdx}` → indices, or null when it isn't one. */
export function parseGadsRowId(rowId) {
  const m = /^gads-(\d+)-(\d+)$/.exec(String(rowId || ''));
  if (!m) return null;
  return { campaignIdx: Number(m[1]), unitIdx: Number(m[2]) };
}

// Resolve a row's owning campaign plus the entity its scalar edits target.
// For Search that's the ad group (or the campaign itself when the plan has no
// ad_groups — then the row IS the campaign, so writing to it is unambiguous).
// For PMax / Demand Gen it's the asset record the campaign references, which
// lives in the top-level `assets` array keyed by asset_key.
function resolveTargets(edited, subtype, rowId) {
  const at = parseGadsRowId(rowId);
  if (!at) return null;
  const campaign = asArray(edited.campaigns)[at.campaignIdx];
  if (!campaign) return null;

  if (subtype === 'search') {
    const adGroups = asArray(campaign.ad_groups);
    const adGroup = adGroups.length ? adGroups[at.unitIdx] : null;
    if (adGroups.length && !adGroup) return null;
    return { campaign, adGroup, asset: null, hasAdGroups: adGroups.length > 0 };
  }

  const inline = asArray(campaign.asset_groups);
  if (inline.length) {
    const asset = inline[at.unitIdx];
    return asset ? { campaign, adGroup: null, asset, hasAdGroups: false } : null;
  }
  const ref = asArray(campaign.assets)[at.unitIdx];
  if (ref && typeof ref === 'object') return { campaign, adGroup: null, asset: ref, hasAdGroups: false };
  const key = ref == null ? null : String(ref).trim();
  if (!key) return { campaign, adGroup: null, asset: null, hasAdGroups: false };
  const asset = asArray(edited.assets).find((a) => String(a?.asset_key || '').trim() === key) || null;
  return { campaign, adGroup: null, asset, hasAdGroups: false };
}

// How many rows a campaign renders — the divisor buildGadsReviewRows uses for
// the per-unit budget share (searchRows / assetGroupRows). Mirrors
// assetGroupsFor's fallbacks so the share × units round-trip is exact.
function gadsUnitCount(edited, campaign, subtype) {
  if (subtype === 'search') return asArray(campaign?.ad_groups).length || 1;
  const inline = asArray(campaign?.asset_groups);
  if (inline.length) return inline.length;
  const refs = asArray(campaign?.assets).filter((r) => (r && typeof r === 'object') || nonEmpty(r));
  return refs.length || 1;
}

// ── Shared writers (also used by the copy grid) ──────────────────────

/**
 * Write a text-asset list to the spelling the entity already uses. The Google
 * Ads creators read both (`headlines` / `headline_texts`), and the row builder
 * prefers whichever the entity carries — so writing the existing key keeps the
 * read and the write on the same array.
 */
export function writeGadsList(entity, primaryKey, altKey, draft) {
  const list = splitListDraft(draft);
  if (Array.isArray(entity[altKey]) && !Array.isArray(entity[primaryKey])) entity[altKey] = list;
  else entity[primaryKey] = list;
}

/**
 * Bid target, parsed from the cell's own display vocabulary
 * (gadsRowBuilders.resolveTargetLabel): "3.5× ROAS" / "3.5x ROAS" → a tROAS
 * multiplier, "$25 CPA" → a tCPA in micros. Returns null for anything else, so
 * an unparseable draft is a no-op.
 *
 * The CPA branch is ANCHORED, matching the ROAS branch above it. It used to be
 * `/cpa/i.test(s) ? numeric(s) : null`, which leaned on the old numeric()
 * stripping every non-digit — so it read a number out of any string that
 * happened to contain "cpa" ("cpa is about 25" → $25 CPA). numeric() now
 * rejects a draft with a unit suffix, so the number is extracted here, from a
 * pattern that states where in the string it is allowed to be.
 */
export function parseGadsTargetLabel(draft) {
  const s = String(draft == null ? '' : draft).trim();
  const roas = /^([\d.]+)\s*[x×]?\s*roas$/i.exec(s) || /^roas\s*[:=]?\s*([\d.]+)$/i.exec(s);
  if (roas && Number.isFinite(Number(roas[1]))) return { roas: Number(roas[1]) };
  const cpaMatch = /^(\$?[\d,.]+)\s*cpa$/i.exec(s) || /^cpa\s*[:=]?\s*(\$?[\d,.]+)$/i.exec(s);
  const cpa = cpaMatch ? numeric(cpaMatch[1]) : null;
  if (cpa != null) return { cpaMicros: Math.round(cpa * 1e6) };
  return null;
}

/**
 * Campaign-level cells shared by Search and asset-group rows: flight dates
 * (native YYYY-MM-DD strings on these templates), daily / total budget micros
 * and the bid target.
 *
 * `opts.budgetUnits` scales the total-budget edit: the CLW grid's budget cell
 * shows the campaign total's PER-UNIT share (buildGadsReviewRows divides by
 * ad-group / asset-group count), so persisting share × units makes the cell
 * re-read as typed. The copy grid's overlay shows the campaign's own total
 * instead, so it leaves the default of 1.
 *
 * `opts.clearRivalTarget` drops the other target key when a target is written
 * (resolveTargetLabel prefers target_roas, so a stale ROAS would otherwise
 * mask a freshly typed CPA). Off by default — the copy grid's behaviour
 * predates it.
 */
export function applyGadsCampaignFields(campaign, fields, opts = {}) {
  const { budgetUnits = 1, clearRivalTarget = false } = opts;
  if (fields.start != null) {
    const v = ymd(fields.start);
    if (v) campaign.start_date = v;
  }
  if (fields.end != null) {
    const v = ymd(fields.end);
    if (v) campaign.end_date = v;
  }
  if (fields.dailyBudget != null) {
    const micros = microsFrom(fields.dailyBudget);
    if (micros != null) campaign.daily_budget_micros = micros;
  }
  if (fields.budget != null) {
    // Through the shared scaleToParent so GADS_SCALED_FIELDS' pre-commit
    // preview derives the identical dollar total (the micros conversion is
    // storage detail on top of it).
    const total = scaleToParent(numeric(fields.budget), budgetUnits);
    if (total != null) campaign.total_budget_micros = Math.round(total * 1e6);
  }
  if (fields.targetLabel != null) {
    const target = parseGadsTargetLabel(fields.targetLabel);
    if (target && target.roas != null) {
      campaign.target_roas = target.roas;
      if (clearRivalTarget) {
        delete campaign.target_cpa_micros;
        delete campaign.target_cpa;
      }
    } else if (target && target.cpaMicros != null) {
      campaign.target_cpa_micros = target.cpaMicros;
      if (clearRivalTarget) delete campaign.target_roas;
    }
  }
}

/**
 * Rebuild a Search keyword list from the ' · '-joined draft. Existing entries
 * (bare strings or {keyword, match_type}) are matched by their rendered text
 * and KEPT — so re-ordering, editing or deleting through the panel never loses
 * a match type. New text gets the list's dominant match_type (what a planner
 * adding "one more keyword like these" means), BROAD when the list is empty.
 */
export function rebuildGadsKeywords(target, draft) {
  const existing = asArray(target.keywords);
  const byText = new Map();
  const typeCounts = {};
  for (const k of existing) {
    const text = keywordText(k);
    if (text && !byText.has(text)) byText.set(text, k);
    const mt = k && typeof k === 'object' && k.match_type ? String(k.match_type).toUpperCase() : null;
    if (mt) typeCounts[mt] = (typeCounts[mt] || 0) + 1;
  }
  const dominant = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'BROAD';
  target.keywords = splitListDraft(draft)
    .map((text) => (byText.has(text) ? byText.get(text) : { keyword: text, match_type: dominant }));
}

/** "/p1/p2" (the displayPath cell's own format) → path1/path2. */
export function applyGadsDisplayPath(target, draft) {
  const parts = String(draft).trim().replace(/^\/+/, '').split('/')
    .map((s) => s.trim()).filter(Boolean);
  target.path1 = parts[0] || '';
  target.path2 = parts[1] || '';
}

// ── Scalar edits ─────────────────────────────────────────────────────

// The URL keys the row builder reads (ag.final_url, then the campaign's
// click_url ?? final_url ?? campaign_url). Write every spelling the entity
// already carries so the read and the write can't diverge; with none present,
// create the one the builder prefers for that entity.
function writeGadsUrl(entity, value, preferred) {
  const url = String(value).trim();
  if (!url) return;
  const present = ['final_url', 'click_url', 'campaign_url'].filter((k) => k in entity);
  if (!present.length) entity[preferred] = url;
  else for (const k of present) entity[k] = url;
}

// Which entity carries a Search row's copy list: the ad group when it states
// one (either spelling), the campaign otherwise — exactly how searchRows picks
// what to render (`ag && (ag.headlines || ag.headline_texts) ? … : campaign…`),
// so an edit lands where the cell read from.
const searchCopyTarget = (campaign, adGroup, keys) => (
  adGroup && keys.some((k) => adGroup[k] != null) ? adGroup : campaign
);

// Demand Gen / PMax targeting bag. resolveTargeting reads campaign.targeting
// when it's an object and falls back to the flat campaign keys, so writes
// follow the same rule — an existing flat list must not be shadowed by a new
// targeting object.
const FLAT_TARGETING_KEYS = [
  'age_ranges', 'genders', 'parental_statuses', 'income_ranges',
  'audience_segments', 'custom_audiences', 'interests',
];
function targetingBag(campaign) {
  if (campaign.targeting && typeof campaign.targeting === 'object') return campaign.targeting;
  if (FLAT_TARGETING_KEYS.some((k) => Array.isArray(campaign[k]))) return campaign;
  campaign.targeting = {};
  return campaign.targeting;
}

// 'Lifetime budget' / 'Daily budget' — the pacing cell's own rendering
// (booleans and bare tokens tolerated). Unrecognized input → no-op.
function applyGadsPacing(campaign, draft) {
  const s = String(draft).trim().toLowerCase();
  if (!s) return;
  if (s.includes('lifetime') || s === 'true') campaign.is_lifetime_budget = true;
  else if (s.includes('daily') || s === 'false') campaign.is_lifetime_budget = false;
}

// Campaign-scoped cells. Every row of the campaign re-reads these, which is
// what the Google Ads entity model dictates — see the module header.
function applyGadsCampaignScoped(campaign, fields, unitCount) {
  applyGadsCampaignFields(campaign, fields, { budgetUnits: unitCount, clearRivalTarget: true });
  if (fields.market != null && String(fields.market).trim()) {
    campaign.name = String(fields.market).trim();
  }
  if (fields.pacing != null) applyGadsPacing(campaign, fields.pacing);
  // NO entityStatus WRITE. `entityStatus` is denylisted on all three subtypes
  // (GADS_SHARED_DENYLIST) and refused here as well, because the denylist is
  // not the only route into this applier — LaunchCampaignDetails'
  // applyFlagSuggestion stages edits by field name without consulting the
  // editable set. Activation is the launch action's job, not a cell; the same
  // rule holds on TTD and DV360 (see reviewEdits.js).
  if (fields.bidStrategy != null && String(fields.bidStrategy).trim()) {
    campaign.bidding_strategy = String(fields.bidStrategy).trim();
  }
  if (fields.countryCode != null && String(fields.countryCode).trim()) {
    campaign.country_code = String(fields.countryCode).trim();
  }
  if (fields.languages != null) campaign.languages = splitCommaDraft(fields.languages);
  // Demographics — the four Demand Gen / PMax targeting lists, rendered as
  // ', '-joined enum values by resolveTargeting.
  const demo = { age: 'age_ranges', gender: 'genders', parental: 'parental_statuses', income: 'income_ranges' };
  const demoEdits = Object.entries(demo).filter(([field]) => fields[field] != null);
  if (demoEdits.length) {
    const bag = targetingBag(campaign);
    for (const [field, key] of demoEdits) bag[key] = splitCommaDraft(fields[field]);
  }
}

function applySearchFields(targets, fields) {
  const { campaign, adGroup, hasAdGroups } = targets;
  const unit = adGroup || campaign;
  if (fields.name != null && String(fields.name).trim()) {
    if (hasAdGroups && adGroup) adGroup.name = String(fields.name).trim();
    // No ad_groups: the row is the campaign, so its name is the campaign's.
    else campaign.name = String(fields.name).trim();
  }
  if (fields.matchType != null && String(fields.matchType).trim() && adGroup) {
    adGroup.match_type = String(fields.matchType).trim().toUpperCase();
  }
  if (fields.bidFloor != null) {
    const micros = microsFrom(fields.bidFloor);
    if (micros != null) {
      if (adGroup) adGroup.max_cpc_bid_micros = micros;
      else campaign.cpc_bid_ceiling_micros = micros;
    }
  }
  if (fields.clickURL != null) {
    writeGadsUrl(unit, fields.clickURL, adGroup ? 'final_url' : 'click_url');
  }
  if (fields.displayPath != null) applyGadsDisplayPath(unit, fields.displayPath);
  if (fields.headlines != null) {
    writeGadsList(searchCopyTarget(campaign, adGroup, ['headlines', 'headline_texts']), 'headline_texts', 'headlines', fields.headlines);
  }
  if (fields.descriptions != null) {
    writeGadsList(searchCopyTarget(campaign, adGroup, ['descriptions', 'description_texts']), 'description_texts', 'descriptions', fields.descriptions);
  }
}

function applyAssetFields(targets, fields) {
  const { campaign, asset } = targets;
  // Asset-group copy lives on the asset record; the final URL is campaign-level
  // (the creators seed it from the first asset group), so keep the asset's own
  // copy in step when it carries one.
  if (fields.clickURL != null) {
    writeGadsUrl(campaign, fields.clickURL, 'final_url');
    if (asset && asset.final_url != null) asset.final_url = String(fields.clickURL).trim();
  }
  if (!asset) return;
  if (fields.longHeadline != null) {
    const v = String(fields.longHeadline);
    // The row builder reads `long_headline` first and falls back to
    // long_headlines[0]; the PMax creator reads the LIST. Write both so the
    // grid and the launch agree.
    asset.long_headline = v;
    if (Array.isArray(asset.long_headlines) && asset.long_headlines.length) asset.long_headlines[0] = v;
    else asset.long_headlines = [v];
  }
  if (fields.businessName != null) {
    asset.business_name = String(fields.businessName);
    if (Array.isArray(asset.business_names) && asset.business_names.length) {
      asset.business_names[0] = asset.business_name;
    }
  }
  if (fields.callToAction != null) asset.call_to_action_text = String(fields.callToAction);
  if (fields.headlines != null) writeGadsList(asset, 'headlines', 'headline_texts', fields.headlines);
  if (fields.descriptions != null) writeGadsList(asset, 'descriptions', 'description_texts', fields.descriptions);
}

// ── List ops ─────────────────────────────────────────────────────────
// Ops are { op: 'remove' | 'add' | 'edit', value, newValue? }. Legacy callers
// (the copy grid) pass bare label arrays, normalized to remove ops by
// normalizeRowOps before we get here.

// Per-row remove ops → the legacy per-row dels shape ({ field: [label] }),
// which the geo / targeting filters below consume.
const removesOf = (fieldOps) => {
  const dels = {};
  for (const [field, ops] of Object.entries(fieldOps)) {
    const removed = ops.filter((o) => o.op === 'remove').map((o) => o.value);
    if (removed.length) dels[field] = removed;
  }
  return dels;
};

// Apply add / edit / remove to a list of plain strings, in place-ish (returns
// the next array). Removes and edits match the rendered label.
function applyStringOps(list, ops, label = (v) => String(v)) {
  let next = asArray(list).slice();
  for (const op of ops) {
    if (op.op === 'remove') next = next.filter((v) => label(v) !== op.value);
    else if (op.op === 'add') next.push(op.value);
    else if (op.op === 'edit' && op.newValue) {
      const i = next.findIndex((v) => label(v) === op.value);
      if (i >= 0) next[i] = op.newValue;
    }
  }
  return next;
}

// The list key an entity carries for a text-asset pair, defaulting to the
// spelling the row builder prefers for that entity kind.
const listKeyOn = (entity, primary, alt) => (
  Array.isArray(entity[alt]) && !Array.isArray(entity[primary]) ? alt : primary
);

// Search keyword ops go through rebuildGadsKeywords so match types survive:
// build the next LABEL list, then let the shared writer re-attach each label's
// original entry (new text inherits the list's dominant match type). The
// entity is the one the row rendered from — the ad group when it owns a
// keyword list (or when the campaign has none either), the campaign otherwise.
function applyKeywordOps(targets, ops) {
  const { campaign, adGroup } = targets;
  const entity = adGroup && (Array.isArray(adGroup.keywords) || !Array.isArray(campaign.keywords))
    ? adGroup : campaign;
  const labels = asArray(entity.keywords).map(keywordText).filter(Boolean);
  const next = applyStringOps(labels, ops);
  rebuildGadsKeywords(entity, next.join(' · '));
}

// Geo lives on the campaign. `locations` / `excluded_locations` hold names;
// `location_ids` hold Google geo-target constants, which the row renders as
// "Geo {id}" — strip that prefix when matching so the label the reviewer
// clicked resolves back to the id. A copy's resolved geo override renders
// under Google's name for the target instead (gadsGeoIncludeEntries), so a
// label is also matched back to the exact entry it was rendered from.
function applyGeoDeletions(campaign, dels) {
  const dropInc = new Set(dels.geoInclude || []);
  const dropExc = new Set(dels.geoExclude || []);
  if (dropInc.size) {
    const hit = gadsGeoIncludeEntries(campaign).filter((e) => dropInc.has(e.label));
    const hitIn = (slot) => new Set(hit.filter((e) => e.slot === slot).map((e) => e.value));
    if (Array.isArray(campaign.locations) && campaign.locations.length) {
      const dropNames = hitIn('locations');
      campaign.locations = campaign.locations.filter(
        (l) => !dropInc.has(String(l)) && !dropNames.has(String(l).trim()));
    }
    if (Array.isArray(campaign.location_ids) && campaign.location_ids.length) {
      const dropIds = new Set([...dropInc].map((l) => String(l).replace(/^Geo\s+/, '')));
      const dropLabelled = hitIn('location_ids');
      campaign.location_ids = campaign.location_ids.filter(
        (id) => !dropIds.has(String(id)) && !dropLabelled.has(String(id).trim()));
    }
  }
  if (dropExc.size && Array.isArray(campaign.excluded_locations)) {
    campaign.excluded_locations = campaign.excluded_locations.filter((l) => !dropExc.has(String(l)));
  }
}

// A geo ADD routes by what was typed: a bare number or the cell's own
// "Geo {id}" rendering is a Google geo-target constant (location_ids);
// anything else is a location NAME, which the launcher resolves at push time.
// Exclusions have no id array in the template — excluded_locations takes both.
function applyGeoAdd(campaign, value, excluded) {
  const s = String(value).trim();
  if (!s) return;
  if (excluded) {
    if (!Array.isArray(campaign.excluded_locations)) campaign.excluded_locations = [];
    campaign.excluded_locations.push(s.replace(/^Geo\s+/, ''));
    return;
  }
  const idMatch = /^Geo\s+(\S+)$/i.exec(s);
  const id = idMatch ? idMatch[1] : (/^\d+$/.test(s) ? s : null);
  if (id) {
    if (!Array.isArray(campaign.location_ids)) campaign.location_ids = [];
    campaign.location_ids.push(id);
    return;
  }
  if (!Array.isArray(campaign.locations)) campaign.locations = [];
  campaign.locations.push(s);
}

function applyGeoOps(campaign, fieldOps) {
  applyGeoDeletions(campaign, removesOf(fieldOps));
  for (const [field, excluded] of [['geoInclude', false], ['geoExclude', true]]) {
    for (const op of (fieldOps[field] || [])) {
      if (op.op === 'add') applyGeoAdd(campaign, op.value, excluded);
      else if (op.op === 'edit' && op.newValue) {
        // Edit = remove(old) + add(new): the old label can live in either the
        // name array or the id array, and the new value re-routes by kind.
        applyGeoDeletions(campaign, { [field]: [op.value] });
        applyGeoAdd(campaign, op.newValue, excluded);
      }
    }
  }
}

// Audience / interest entries can be strings or objects, so match on the same
// label the row rendered (resolveTargeting).
const targetingLabel = (v) => (v && typeof v === 'object'
  ? String(v.name ?? v.id ?? v.resource_name ?? '')
  : String(v ?? ''));

// Demand Gen / PMax audience + interest lists live under campaign.targeting
// (or flat on the campaign, which the row builder also tolerates).
function applyTargetingListOps(campaign, fieldOps) {
  const audOps = fieldOps.audiences || [];
  const intOps = fieldOps.interests || [];
  if (!audOps.length && !intOps.length) return;
  const bag = targetingBag(campaign);
  if (audOps.length) {
    // Removes hit both audience arrays the cell unions; adds/edits target
    // audience_segments (plain names — the launcher resolves them).
    const removes = audOps.filter((o) => o.op === 'remove');
    if (removes.length && Array.isArray(bag.custom_audiences)) {
      bag.custom_audiences = applyStringOps(bag.custom_audiences, removes, targetingLabel);
    }
    bag.audience_segments = applyStringOps(bag.audience_segments, audOps, targetingLabel);
  }
  if (intOps.length) {
    bag.interests = applyStringOps(bag.interests, intOps, targetingLabel);
  }
}

// Sitelinks are {link_text, final_url} objects in the real templates (bare
// strings tolerated). The cell lists the link text, so that's what removes and
// edits match; an add joins the list in whichever shape it already uses.
const sitelinkLabel = (s) => (s && typeof s === 'object'
  ? String(s.link_text ?? s.text ?? s.name ?? '')
  : String(s ?? ''));

// The fields a sitelink editor may write. link_text is handled separately (it is
// the match key); the rest are carried straight onto the entry, which is exactly
// the shape SearchCampaignCreator.create_sitelink_assets reads — including the
// per-sitelink tracking pair, which is how one link can be tagged differently
// from the campaign.
const SITELINK_FIELDS = ['final_url', 'tracking_url_template', 'final_url_suffix',
  'description1', 'description2'];

const sitelinkFields = (op) => {
  const out = {};
  for (const k of SITELINK_FIELDS) {
    const v = op?.fields?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') out[k] = String(v).trim();
  }
  return out;
};

// A sitelink with no final_url is DROPPED at launch, not flagged:
// create_sitelink_assets skips any entry missing link_text or final_url and
// continues. So an added sitelink that carries only its text renders in the
// grid, saves, survives the push — and never reaches Google, with nothing on
// screen saying so. Falling back to the campaign's own landing page keeps the
// link launchable, and the review step shows the resulting URL so the default
// is visible rather than assumed.
const entityFallbackUrl = (entity) => {
  const direct = entity?.final_url || entity?.click_url;
  if (direct) return String(direct);
  const existing = asArray(entity?.sitelinks).find((s) => s && typeof s === 'object' && s.final_url);
  return existing ? String(existing.final_url) : '';
};

// A UTM typed into a sitelink's suffix box goes onto its final URL, the way the
// copy build writes them (copy_url_tracking.inline_sitelink_utms) and the way
// sitelinks read in Google Ads. Same two guards as the build: a suffix that masks
// the campaign's is left alone when nothing else would remain in it, and a
// `{lpurl}?` template over the now-queried URL becomes the sitelink's own `{lpurl}&`.
const queryKey = (part) => {
  const raw = part.split('=', 1)[0].replace(/\+/g, ' ');
  try { return decodeURIComponent(raw).toLowerCase(); } catch { return raw.toLowerCase(); }
};

function inlineSitelinkUtms(sitelink, entity) {
  const url = String(sitelink.final_url || '');
  const parts = String(sitelink.final_url_suffix || '').trim().replace(/^[?&]+/, '')
    .split('&').filter(Boolean);
  const utms = parts.filter((p) => queryKey(p).startsWith('utm_'));
  const remaining = parts.filter((p) => !queryKey(p).startsWith('utm_'));
  if (!url || !utms.length) return;
  if (!remaining.length && entity?.final_url_suffix) return;

  const [withoutFragment, ...fragment] = url.split('#');
  const [base, ...queryParts] = withoutFragment.split('?');
  const query = queryParts.join('?').split('&').filter(Boolean);
  const positions = new Map(query.map((p, i) => [queryKey(p), i]));
  utms.forEach((p) => {
    const k = queryKey(p);
    if (positions.has(k)) query[positions.get(k)] = p;
    else { positions.set(k, query.length); query.push(p); }
  });

  const template = String(sitelink.tracking_url_template || entity?.tracking_url_template || '').trim();
  if (template.startsWith('{lpurl}?') && !url.includes('?')) {
    const rest = template.slice('{lpurl}?'.length);
    sitelink.tracking_url_template = rest ? `{lpurl}&${rest}` : '{lpurl}';
  }
  sitelink.final_url = `${base}?${query.join('&')}${fragment.length ? `#${fragment.join('#')}` : ''}`;
  if (remaining.length) sitelink.final_url_suffix = remaining.join('&');
  else delete sitelink.final_url_suffix;
}

function applySitelinkOps(entity, ops) {
  let list = asArray(entity.sitelinks).slice();
  const objectShaped = list.some((s) => s && typeof s === 'object');
  for (const op of ops) {
    if (op.op === 'remove') {
      list = list.filter((s) => sitelinkLabel(s) !== op.value);
    } else if (op.op === 'add') {
      const fields = sitelinkFields(op);
      // An add stays a bare string only when the list is already bare strings AND
      // the editor supplied nothing else to keep.
      if (!objectShaped && Object.keys(fields).length === 0) {
        list.push(op.value);
      } else {
        const fallback = entityFallbackUrl(entity);
        const added = {
          link_text: op.value,
          ...(fields.final_url || !fallback ? {} : { final_url: fallback }),
          ...fields,
        };
        if (fields.final_url_suffix) inlineSitelinkUtms(added, entity);
        list.push(added);
      }
    } else if (op.op === 'edit') {
      const i = list.findIndex((s) => sitelinkLabel(s) === op.value);
      if (i < 0) continue;
      const fields = sitelinkFields(op);
      // newValue renames; fields rewrite the URL/tracking. Either may be absent —
      // an edit that only sets a suffix must not blank the link text.
      if (!op.newValue && Object.keys(fields).length === 0) continue;
      const hit = list[i];
      if (hit && typeof hit === 'object') {
        list[i] = { ...hit, ...(op.newValue ? { link_text: op.newValue } : {}), ...fields };
      } else if (Object.keys(fields).length > 0) {
        list[i] = { link_text: op.newValue || sitelinkLabel(hit), ...fields };
      } else {
        list[i] = op.newValue;
      }
      if (fields.final_url_suffix && list[i] && typeof list[i] === 'object') {
        inlineSitelinkUtms(list[i], entity);
      }
    }
  }
  entity.sitelinks = list;
}

// Campaign-scoped for every subtype — see the call site for why. An ad group
// that carries its own list (Search only) keeps owning it.
function applySitelinkListOps({ campaign, adGroup }, fieldOps) {
  if (!fieldOps.sitelinks?.length) return;
  applySitelinkOps(adGroup && adGroup.sitelinks != null ? adGroup : campaign, fieldOps.sitelinks);
}

// Images: assetMediaCounts unions four singular keys, four plural arrays and
// `images`, so a REMOVE has to find the label wherever it lives (clearing a
// singular key, filtering an array); an ADD only ever writes `images`, the one
// key that is a plain list of URLs.
const ASSET_IMAGE_SINGULAR = ['logo_image', 'marketing_image', 'square_marketing_image', 'portrait_marketing_image'];
const ASSET_IMAGE_ARRAYS = ['logo_images', 'marketing_images', 'square_marketing_images', 'portrait_marketing_images', 'images'];
const mediaLabel = (v) => (v && typeof v === 'object'
  ? String(v.url ?? v.asset ?? v.id ?? v.resource_name ?? '')
  : String(v ?? ''));

function removeAssetImage(asset, label) {
  for (const k of ASSET_IMAGE_SINGULAR) {
    if (asset[k] != null && String(asset[k]) === label) delete asset[k];
  }
  for (const k of ASSET_IMAGE_ARRAYS) {
    if (Array.isArray(asset[k])) asset[k] = asset[k].filter((v) => mediaLabel(v) !== label);
  }
}

function applyImageOps(asset, ops) {
  for (const op of ops) {
    if (op.op === 'remove') removeAssetImage(asset, op.value);
    else if (op.op === 'add') {
      if (!Array.isArray(asset.images)) asset.images = [];
      asset.images.push(op.value);
    } else if (op.op === 'edit' && op.newValue) {
      // Rewrite in place where the label lives — a singular key keeps its slot
      // (the creators read those by name), an array entry keeps its position.
      let replaced = false;
      for (const k of ASSET_IMAGE_SINGULAR) {
        if (asset[k] != null && String(asset[k]) === op.value) { asset[k] = op.newValue; replaced = true; }
      }
      for (const k of ASSET_IMAGE_ARRAYS) {
        if (!Array.isArray(asset[k])) continue;
        const i = asset[k].findIndex((v) => mediaLabel(v) === op.value);
        if (i >= 0) { asset[k][i] = op.newValue; replaced = true; }
      }
      if (!replaced) {
        if (!Array.isArray(asset.images)) asset.images = [];
        asset.images.push(op.newValue);
      }
    }
  }
}

// Search list ops — keywords / negative keywords / copy lists / sitelinks live
// on the ad group (or the campaign when the row IS the campaign); zip codes and
// geo are campaign-scoped.
function applySearchListOps(targets, fieldOps) {
  const { campaign, adGroup } = targets;
  const unit = adGroup || campaign;
  if (fieldOps.keywords?.length) applyKeywordOps(targets, fieldOps.keywords);
  if (fieldOps.negativeKeywords?.length) {
    unit.negative_keywords = applyStringOps(unit.negative_keywords, fieldOps.negativeKeywords, (v) => keywordText(v) || '');
  }
  for (const [field, primary, alt] of [
    ['headlines', 'headline_texts', 'headlines'],
    ['descriptions', 'description_texts', 'descriptions'],
  ]) {
    if (!fieldOps[field]?.length) continue;
    const target = searchCopyTarget(campaign, adGroup, [primary, alt]);
    const key = listKeyOn(target, primary, alt);
    target[key] = applyStringOps(target[key], fieldOps[field]);
  }
  if (fieldOps.zipcodes?.length) campaign.zipcodes = applyStringOps(campaign.zipcodes, fieldOps.zipcodes);
}

// PMax / Demand Gen list ops — copy, search themes and media live on the asset
// record; audiences / interests / geo are campaign-scoped.
function applyAssetListOps(targets, fieldOps) {
  const { asset } = targets;
  if (!asset) return;
  // PMax search themes ride the grid's `keywords` cell (see buildGadsReviewRows).
  if (fieldOps.keywords?.length) {
    asset.search_themes = applyStringOps(asset.search_themes, fieldOps.keywords);
  }
  for (const [field, primary, alt] of [
    ['headlines', 'headlines', 'headline_texts'],
    ['descriptions', 'descriptions', 'description_texts'],
  ]) {
    if (!fieldOps[field]?.length) continue;
    const key = listKeyOn(asset, primary, alt);
    asset[key] = applyStringOps(asset[key], fieldOps[field]);
  }
  // The images cell's field key is `creative` (the shared grid vocabulary).
  if (fieldOps.creative?.length) applyImageOps(asset, fieldOps.creative);
  if (fieldOps.videos?.length) {
    asset.videos = applyStringOps(asset.videos, fieldOps.videos, mediaLabel);
  }
}

// ── Entry point ──────────────────────────────────────────────────────

/**
 * Apply pending edits + list ops to an already-cloned Google Ads launcher
 * result, in place.
 *
 * `listOpsOrDeletions` accepts BOTH shapes the grids produce:
 *   - list ops (current): { rowId: { field: [{ op, value, newValue? }] } }
 *   - legacy deletions:   { rowId: { field: [label] } }  (remove-only)
 * Legacy string arrays normalize to remove ops, so removes behave exactly as
 * they did before adds/edits existed.
 *
 * `reviewRows` is used only to skip rowIds the current grid doesn't know
 * about, matching applyLauncherResultEdits' contract.
 */
export function applyGadsEdits(edited, edits, listOpsOrDeletions, reviewRows = [], rowOps = []) {
  const subtype = gadsResultSubtype(edited);
  const known = reviewRows.length ? new Set(reviewRows.map((r) => r.rowId)) : null;

  for (const [rowId, fields] of Object.entries(edits || {})) {
    if (known && !known.has(rowId)) continue;
    const targets = resolveTargets(edited, subtype, rowId);
    if (!targets) continue;
    applyGadsCampaignScoped(targets.campaign, fields, gadsUnitCount(edited, targets.campaign, subtype));
    if (subtype === 'search') applySearchFields(targets, fields);
    else applyAssetFields(targets, fields);
  }

  for (const [rowId, fields] of Object.entries(listOpsOrDeletions || {})) {
    const targets = resolveTargets(edited, subtype, rowId);
    if (!targets) continue;
    const fieldOps = normalizeRowOps(fields);
    if (subtype === 'search') applySearchListOps(targets, fieldOps);
    else applyAssetListOps(targets, fieldOps);
    // Sitelinks are CAMPAIGN-level on EVERY Google Ads type, so they belong here
    // beside the other campaign-scoped ops rather than inside the Search branch.
    // Verified account-wide: 2,097 sitelinks across 152 campaigns (Search, PMax,
    // Demand Gen, Video) and zero at asset-group, ad-group or account level.
    //
    // Living in applySearchListOps meant a sitelink op on a PMax copy was accepted
    // by the grid, saved, and applied to nothing — the silent no-op class this
    // module is most prone to, since an unhandled field simply falls through.
    // Search ad groups CAN hold their own list, so that case still wins when the
    // ad group actually has one.
    applySitelinkListOps(targets, fieldOps);
    applyGeoOps(targets.campaign, fieldOps);
    applyTargetingListOps(targets.campaign, fieldOps);
  }

  // Row ops last. Google Ads rows are addressed positionally (`gads-${ci}-${ai}`)
  // because the template carries no pre-launch ids, so a delete renumbers its
  // siblings — resolving the value edits above first is what keeps them pointing
  // at the rows the user actually saw. Clone overrides then go through the same
  // field appliers as a typed edit.
  if (Array.isArray(rowOps) && rowOps.length) {
    const { created } = applyGadsRowOps(edited, rowOps);
    for (const { rowId, overrides } of created) {
      if (!overrides || !Object.keys(overrides).length) continue;
      const targets = resolveTargets(edited, subtype, rowId);
      if (!targets) continue;
      applyGadsCampaignScoped(targets.campaign, overrides, gadsUnitCount(edited, targets.campaign, subtype));
      if (subtype === 'search') applySearchFields(targets, overrides);
      else applyAssetFields(targets, overrides);
    }
  }

  return edited;
}
